import React, { useEffect, useState, useCallback } from "react";
import styled from "styled-components";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { Search, Eye, Trash2, ChevronLeft, ChevronRight } from "lucide-react";
import ApiCollections from "../../configs/services/apiCollections";
import { signOutRequest } from "../../redux/auth/auth.actions";
import { toastify } from "../../configs/utils";

// Styled Components
const BatchesContainer = styled.section`
  background: #ededed;
  border-radius: 0;
  border: none;
  border-top: 0;
  border-bottom-left-radius: 12px;
  border-bottom-right-radius: 12px;
  padding: 2rem;
  box-shadow: none;
  width: 100%;
  min-height: 100%;
  overflow-y: auto;
`;

const BatchesControls = styled.div`
  display: flex;
  align-items: center;
  gap: 1.25rem;
  margin-bottom: 1.75rem;
  padding: 1.125rem 1.5rem;
  background: transparent;
  border-radius: 12px;
  border: 1px solid rgba(17, 24, 39, 0.08);
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04), 0 6px 16px rgba(0, 0, 0, 0.07),
    inset 0 1px 0 rgba(255, 255, 255, 0.6);
  flex-wrap: wrap;
  font-family: "IBM Plex Sans", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto,
    sans-serif;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
    gap: 0.875rem;
    padding: 1rem;
  }
`;

const ControlGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 0.625rem;

  @media (max-width: 768px) {
    width: 100%;
    justify-content: space-between;
  }
`;

const ControlLabel = styled.label`
  font-size: 0.8125rem;
  font-weight: 600;
  color: #4b5563;
  white-space: nowrap;
  letter-spacing: 0.01em;
`;

const ControlSelect = styled.select`
  padding: 0.5rem 2rem 0.5rem 0.875rem;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  background: transparent
    url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='6' viewBox='0 0 10 6' fill='none'%3E%3Cpath d='M1 1L5 5L9 1' stroke='%236B7280' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E")
    no-repeat right 0.75rem center;
  appearance: none;
  font-family: inherit;
  font-size: 0.875rem;
  font-weight: 500;
  color: #4b5563;
  cursor: pointer;
  outline: none;
  min-width: 150px;
  height: 2.5rem;
  transition: border-color 0.15s ease, box-shadow 0.15s ease,
    background-color 0.15s ease;

  &:focus {
    background-color: #ffffff;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.12);
  }

  &:hover {
    background-color: #ffffff;
    border-color: #9ca3af;
  }

  option {
    color: #4b5563;
    font-weight: 400;
  }

  @media (max-width: 768px) {
    width: 100%;
    min-width: unset;
  }
`;

const SearchInputWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;

  svg {
    position: absolute;
    left: 0.75rem;
    color: #9ca3af;
    pointer-events: none;
  }

  @media (max-width: 768px) {
    width: 100%;
  }
`;

const ControlInput = styled.input`
  padding: 0.5rem 0.875rem 0.5rem 2.25rem;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  background: transparent;
  font-family: inherit;
  font-size: 0.875rem;
  font-weight: 500;
  color: #1f2937;
  outline: none;
  min-width: 240px;
  height: 2.5rem;
  transition: border-color 0.15s ease, box-shadow 0.15s ease,
    background-color 0.15s ease;

  &:focus {
    background-color: #ffffff;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.12);
  }

  &:hover {
    background-color: #ffffff;
    border-color: #9ca3af;
  }

  &::placeholder {
    color: #9ca3af;
    font-weight: 400;
  }

  @media (max-width: 768px) {
    width: 100%;
    min-width: unset;
  }
`;

const ControlsSpacer = styled.div`
  flex: 1;

  @media (max-width: 768px) {
    display: none;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-left: auto;

  @media (max-width: 768px) {
    width: 100%;
    margin-left: 0;
    flex-direction: column;
  }
`;

const RefreshButton = styled.button`
  padding: 0.375rem 0.875rem;
  height: 2.125rem;
  background: transparent;
  color: #374151;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-family: inherit;
  font-size: 0.8125rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.375rem;
  white-space: nowrap;

  &:hover:not(.disabled) {
    border-color: #9ca3af;
    background: #ffffff;
  }

  &:active:not(.disabled) {
    background: #f3f4f6;
  }

  &.disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  @media (max-width: 768px) {
    width: 100%;
  }
`;

const CreateBatchButton = styled.button`
  padding: 0.375rem 0.875rem;
  height: 2.125rem;
  background: #3b82f6;
  color: #ffffff;
  border: 1px solid #3b82f6;
  border-radius: 6px;
  font-family: inherit;
  font-size: 0.8125rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.375rem;
  white-space: nowrap;
  box-shadow: 0 1px 2px rgba(59, 130, 246, 0.2);

  &:hover {
    background: #2563eb;
    border-color: #2563eb;
  }

  &:active {
    background: #1d4ed8;
  }

  @media (max-width: 768px) {
    width: 100%;
  }
`;

const BatchesStatus = styled.p`
  text-align: center;
  padding: 2rem;
  color: #6b7280;
  font-size: 1.1rem;
`;

const BatchesError = styled.p`
  text-align: center;
  padding: 2rem;
  color: #ef4444;
  font-size: 1.1rem;
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 8px;
  margin: 1rem 0;
`;

const TableContainer = styled.div`
  margin-bottom: 2rem;
  width: 100%;
  box-sizing: border-box;
  overflow: hidden;
  position: relative;
  background: transparent;
  border: 1px solid rgba(17, 24, 39, 0.08);
  border-radius: 12px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04), 0 6px 16px rgba(0, 0, 0, 0.07),
    inset 0 1px 0 rgba(255, 255, 255, 0.6);
`;

const TableScrollArea = styled.div`
  max-height: 70vh;
  width: 100%;
  box-sizing: border-box;
  overflow-y: auto;
  overflow-x: auto;

  /* Scrollable, scrollbar never shown */
  scrollbar-width: none;
  -ms-overflow-style: none;

  &::-webkit-scrollbar {
    display: none;
  }
`;

const Table = styled.table`
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  font-family: "IBM Plex Sans", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto,
    sans-serif;
  font-size: 0.875rem;
  min-width: 800px;
  background: transparent;
  margin: 0;
`;

const TableHead = styled.thead`
  background: #ededed;
  position: sticky;
  top: 0;
  z-index: 25;
  display: table-header-group;
  width: 100%;
`;

const TableHeader = styled.th`
  padding: 0.75rem 1.25rem;
  text-align: left;
  font-weight: 600;
  border-bottom: 1px solid rgba(17, 24, 39, 0.16);
  white-space: nowrap;
  font-size: 14px;
  letter-spacing: 0.01em;
  color: #4b5563;
  background: #ededed;
  position: sticky;
  top: 0;
  z-index: 25;
`;

const TableBody = styled.tbody``;

const TableRow = styled.tr`
  transition: background-color 0.15s ease;
  cursor: pointer;

  &:hover td {
    background-color: rgba(17, 24, 39, 0.03);
  }

  &:last-child td {
    border-bottom: none;
  }
`;

const TableCell = styled.td`
  padding: 0.875rem 1.25rem;
  vertical-align: middle;
  white-space: nowrap;
  background: #f3f3f3;
  font-size: 0.875rem;
  font-weight: 400;
  color: #4b5563;
  border-bottom: 1px solid rgba(17, 24, 39, 0.08);
`;

const IdColumn = styled(TableCell)`
  width: 70px;
  min-width: 70px;
  color: #6b7280;
`;

const NameColumn = styled(TableCell)`
  width: 220px;
  min-width: 220px;
  font-weight: 500;
  color: #111827;
`;

const CreatedAtColumn = styled(TableCell)`
  width: 150px;
  min-width: 150px;
`;

const CardsCountColumn = styled(TableCell)`
  width: 100px;
  min-width: 100px;
  text-align: center;
`;

const ActionsColumn = styled(TableCell)`
  width: 90px;
  min-width: 90px;
`;

const InlineInput = styled.input`
  flex: 1;
  padding: 0.375rem 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 0.875rem;
  font-weight: 500;
  color: #374151;
  background: white;
  outline: none;
  transition: all 0.2s ease;

  &:focus {
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }

  &:hover {
    border-color: #9ca3af;
  }
`;

const EditContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  width: 100%;
`;

const RowActions = styled.div`
  display: inline-flex;
  gap: 0.5rem;
`;

const SmallButton = styled.button`
  padding: 0.3125rem 0.75rem;
  border: 1px solid
    ${(props) =>
      props.variant === "danger"
        ? "#fecaca"
        : props.variant === "secondary"
        ? "#e5e7eb"
        : "#bfdbfe"};
  border-radius: 6px;
  font-family: inherit;
  font-size: 0.8125rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s ease;
  background: ${(props) =>
    props.variant === "danger"
      ? "#fef2f2"
      : props.variant === "secondary"
      ? "#f9fafb"
      : "#eff6ff"};
  color: ${(props) =>
    props.variant === "danger"
      ? "#dc2626"
      : props.variant === "secondary"
      ? "#4b5563"
      : "#2563eb"};

  &:hover {
    background: ${(props) =>
      props.variant === "danger"
        ? "#fee2e2"
        : props.variant === "secondary"
        ? "#f3f4f6"
        : "#dbeafe"};
    border-color: ${(props) =>
      props.variant === "danger"
        ? "#fca5a5"
        : props.variant === "secondary"
        ? "#d1d5db"
        : "#93c5fd"};
  }

  &:active {
    transform: translateY(0.5px);
  }
`;

const IconButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 2rem;
  height: 2rem;
  padding: 0;
  border: 1px solid
    ${(props) => (props.variant === "danger" ? "#fecaca" : "#bfdbfe")};
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.15s ease;
  background: ${(props) =>
    props.variant === "danger" ? "#fef2f2" : "#eff6ff"};
  color: ${(props) => (props.variant === "danger" ? "#dc2626" : "#2563eb")};

  svg {
    display: block;
    width: 15px;
    height: 15px;
    stroke: currentColor;
    flex-shrink: 0;
  }

  &:hover {
    background: ${(props) =>
      props.variant === "danger" ? "#fee2e2" : "#dbeafe"};
    border-color: ${(props) =>
      props.variant === "danger" ? "#fca5a5" : "#93c5fd"};
  }

  &:active {
    transform: translateY(0.5px);
  }
`;

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 8px;
  padding: 1.5rem;
  max-width: 400px;
  width: 90%;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1),
    0 10px 10px -5px rgba(0, 0, 0, 0.04);
`;

const ModalTitle = styled.h3`
  margin: 0 0 1rem 0;
  color: #111827;
  font-size: 1.125rem;
  font-weight: 600;
`;

const ModalMessage = styled.p`
  margin: 0 0 1.5rem 0;
  color: #6b7280;
  font-size: 0.875rem;
  line-height: 1.5;
`;

const ModalActions = styled.div`
  display: flex;
  gap: 0.75rem;
  justify-content: flex-end;
`;

const ModalButton = styled.button`
  padding: 0.5rem 1rem;
  border: none;
  border-radius: 6px;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;

  ${(props) =>
    props.variant === "danger"
      ? `
    background: #ef4444;
    color: white;
    &:hover {
      background: #dc2626;
    }
  `
      : `
    background: #f3f4f6;
    color: #374151;
    &:hover {
      background: #e5e7eb;
    }
  `}
`;

const MetadataColumn = styled(TableCell)`
  width: 300px;
  min-width: 300px;
  max-width: 400px;
`;

const BatchesPagination = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.125rem;
  margin-top: 1.75rem;
  font-family: "IBM Plex Sans", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto,
    sans-serif;
`;

const PaginationArrow = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2rem;
  height: 2rem;
  padding: 0;
  margin: 0 0.375rem;
  background: transparent;
  color: #6b7280;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.15s ease;

  svg {
    display: block;
    width: 15px;
    height: 15px;
  }

  &:hover:not(.disabled) {
    background: #f3f4f6;
    color: #111827;
  }

  &:active:not(.disabled) {
    transform: translateY(0.5px);
  }

  &.disabled {
    opacity: 0.35;
    cursor: not-allowed;
  }
`;

const PageButton = styled.button`
  box-sizing: border-box;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 2rem;
  height: 2rem;
  padding: 0 0.375rem;
  background: ${(props) => (props.$active ? "#f3f4f6" : "transparent")};
  color: ${(props) => (props.$active ? "#111827" : "#374151")};
  border: 1px solid ${(props) => (props.$active ? "rgba(17, 24, 39, 0.12)" : "transparent")};
  border-radius: 8px;
  font-family: inherit;
  font-size: 0.8125rem;
  font-weight: ${(props) => (props.$active ? 600 : 500)};
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    background: #f3f4f6;
  }
`;

const PageEllipsis = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 2rem;
  height: 2rem;
  color: #9ca3af;
  font-size: 0.8125rem;
`;

export default function Batches() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [batches, setBatches] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [nextPage, setNextPage] = useState(null);
  const [prevPage, setPrevPage] = useState(null);
  const [count, setCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(10);
  const [ordering, setOrdering] = useState("-created_at");
  const [search, setSearch] = useState("");
  const [editingBatchId, setEditingBatchId] = useState(null);
  const [editNameValue, setEditNameValue] = useState("");
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [batchToDelete, setBatchToDelete] = useState(null);

  const ensureHttps = (url) => {
    if (!url) return null;
    if (url.startsWith("http://") && !url.includes("localhost")) {
      return url.replace("http://", "https://");
    }
    return url;
  };

  const DEFAULT_BASE_URL = "http://localhost:8000";
  const API_BASE_URL = ensureHttps(
    import.meta.env.VITE_BASE_URL_DEVELOPMENT || DEFAULT_BASE_URL
  );

  const refreshAccessToken = async () => {
    const refreshToken = localStorage.getItem("refresh");
    if (!refreshToken) {
      // Show toast message and logout when no refresh token is found
      toastify("error", "No refresh access found. Please login again.");
      dispatch(signOutRequest({ func: () => {} }));
      throw new Error("No refresh token found. Please login again.");
    }

    const refreshResponse = await fetch(
      `${API_BASE_URL}/api/v1/token/refresh/`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ refresh: refreshToken }),
      }
    );

    if (!refreshResponse.ok) {
      localStorage.removeItem("access");
      localStorage.removeItem("refresh");
      // Show toast message and logout when refresh token fails
      toastify("error", "No refresh access found. Please login again.");
      dispatch(signOutRequest({ func: () => {} }));
      throw new Error("Failed to refresh token. Please login again.");
    }

    const refreshData = await refreshResponse.json();
    localStorage.setItem("access", refreshData.access);
    return refreshData.access;
  };

  const extractPageNumber = (url) => {
    if (!url) return null;
    const params = new URL(url).searchParams;
    return parseInt(params.get("page") || "1", 10);
  };

  const normalizePageUrl = (url) => {
    if (!url) return null;
    try {
      const parsed = new URL(url);
      return `${API_BASE_URL}${parsed.pathname}${parsed.search}`;
    } catch {
      return url;
    }
  };

  const buildBaseListUrl = () => {
    const params = new URLSearchParams();
    params.set("page_size", String(pageSize));
    if (ordering && ordering.length > 0) params.set("ordering", ordering);
    if (search && search.trim().length > 0) params.set("search", search.trim());

    return `${API_BASE_URL}/api/v1/cards/batches/?${params.toString()}`;
  };

  const buildDetailUrl = (id) => `${API_BASE_URL}/api/v1/cards/batches/${id}/`;

  const buildPageUrl = (page) => `${buildBaseListUrl()}&page=${page}`;

  const getPageNumbers = (current, total) => {
    const boundaryCount = 2;
    const siblingCount = 1;

    if (total <= boundaryCount * 2 + siblingCount * 2 + 3) {
      return Array.from({ length: total }, (_, i) => i + 1);
    }

    const startPages = Array.from({ length: boundaryCount }, (_, i) => i + 1);
    const endPages = Array.from(
      { length: boundaryCount },
      (_, i) => total - boundaryCount + i + 1
    );

    const leftSibling = Math.max(current - siblingCount, boundaryCount + 1);
    const rightSibling = Math.min(
      current + siblingCount,
      total - boundaryCount
    );

    const showLeftEllipsis = leftSibling > boundaryCount + 2;
    const showRightEllipsis = rightSibling < total - boundaryCount - 1;

    const pages = [...startPages];

    if (showLeftEllipsis) {
      pages.push("start-ellipsis");
    } else if (leftSibling === boundaryCount + 2) {
      pages.push(boundaryCount + 1);
    }

    for (let i = leftSibling; i <= rightSibling; i++) {
      if (!pages.includes(i)) pages.push(i);
    }

    if (showRightEllipsis) {
      pages.push("end-ellipsis");
    } else if (rightSibling === total - boundaryCount - 1) {
      pages.push(total - boundaryCount);
    }

    pages.push(...endPages);

    return pages;
  };

  const fetchBatches = useCallback(
    async (url = null, retry = false) => {
      try {
        setIsLoading(true);
        setError("");

        let token = localStorage.getItem("access");
        if (!token) token = await refreshAccessToken();

        const requestUrl = url ?? buildBaseListUrl();

        let response = await fetch(requestUrl, {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        });

        if (response.status === 401 && !retry) {
          const newToken = await refreshAccessToken();
          response = await fetch(requestUrl, {
            headers: {
              Authorization: `Bearer ${newToken}`,
              "Content-Type": "application/json",
            },
          });
        }

        if (!response.ok)
          throw new Error(`Failed to fetch batches: ${response.status}`);

        const data = await response.json();
        setBatches(Array.isArray(data.results) ? data.results : []);
        setNextPage(normalizePageUrl(data.next));
        setPrevPage(normalizePageUrl(data.previous));
        setCount(data.count);

        // Determine current page
        const pageFromNext = extractPageNumber(normalizePageUrl(data.next));
        const pageFromPrev = extractPageNumber(normalizePageUrl(data.previous));

        if (!pageFromNext && !pageFromPrev) {
          setCurrentPage(1);
        } else if (pageFromPrev) {
          setCurrentPage(pageFromPrev + 1);
        } else if (pageFromNext) {
          setCurrentPage(pageFromNext - 1);
        }
      } catch (err) {
        setError(err?.message || "Something went wrong fetching batches");
      } finally {
        setIsLoading(false);
      }
    },
    [pageSize, ordering, search]
  );

  useEffect(() => {
    fetchBatches();
  }, [fetchBatches]);

  useEffect(() => {
    const handleBatchesRefresh = () => {
      fetchBatches(null, false);
    };

    window.addEventListener("batches-list-refresh", handleBatchesRefresh);
    return () => {
      window.removeEventListener("batches-list-refresh", handleBatchesRefresh);
    };
  }, [fetchBatches]);

  const handleOrderingChange = (e) => {
    const value = e.target.value;
    setOrdering(value);
    // Removed immediate fetchBatches call - useEffect will handle refetch when ordering changes
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    fetchBatches(null, false);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const startEditingName = (batch) => {
    setEditingBatchId(batch.id);
    setEditNameValue(batch.name || "");
  };

  const cancelEditingName = () => {
    setEditingBatchId(null);
    setEditNameValue("");
  };

  const saveEditingName = async (batch) => {
    try {
      let token = localStorage.getItem("access");
      if (!token) token = await refreshAccessToken();

      const url = buildDetailUrl(batch.id);
      let response = await fetch(url, {
        method: "PATCH",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ name: editNameValue }),
      });

      if (response.status === 401) {
        const newToken = await refreshAccessToken();
        response = await fetch(url, {
          method: "PATCH",
          headers: {
            Authorization: `Bearer ${newToken}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ name: editNameValue }),
        });
      }

      if (!response.ok)
        throw new Error(`Failed to update batch: ${response.status}`);

      const updated = await response.json();
      setBatches((prev) =>
        prev.map((b) => (b.id === batch.id ? { ...b, name: updated.name } : b))
      );
      cancelEditingName();
    } catch (e) {
      setError(e?.message || "Failed to update batch name");
    }
  };

  const openDeleteModal = (batch) => {
    setBatchToDelete(batch);
    setDeleteModalOpen(true);
  };

  const closeDeleteModal = () => {
    setDeleteModalOpen(false);
    setBatchToDelete(null);
  };

  const confirmDelete = async () => {
    if (!batchToDelete) return;

    try {
      let token = localStorage.getItem("access");
      if (!token) token = await refreshAccessToken();

      const url = buildDetailUrl(batchToDelete.id);
      let response = await fetch(url, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.status === 401) {
        const newToken = await refreshAccessToken();
        response = await fetch(url, {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${newToken}`,
          },
        });
      }

      if (response.status !== 204 && response.status !== 200) {
        throw new Error(`Failed to delete batch: ${response.status}`);
      }

      setBatches((prev) => prev.filter((b) => b.id !== batchToDelete.id));
      toastify("success", "Batch deleted successfully");
      closeDeleteModal();
    } catch (e) {
      setError(e?.message || "Failed to delete batch");
      closeDeleteModal();
    }
  };

  const formatMetaValue = (value) => {
    if (value == null) return "";
    if (
      typeof value === "string" ||
      typeof value === "number" ||
      typeof value === "boolean"
    ) {
      return String(value);
    }
    if (Array.isArray(value)) {
      return value
        .map((v) => (typeof v === "object" ? JSON.stringify(v) : String(v)))
        .join(", ");
    }
    try {
      return JSON.stringify(value);
    } catch {
      return "[object]";
    }
  };

  const renderMetadata = (metadata) => {
    if (!metadata || typeof metadata !== "object") return "No metadata";
    const entries = Object.entries(metadata);
    if (entries.length === 0) return "No metadata";
    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: "4px",
          maxWidth: "480px",
        }}
      >
        {entries.map(([key, val]) => (
          <div key={key} style={{ display: "flex", gap: "6px" }}>
            <span style={{ color: "#6b7280" }}>{key}:</span>
            <span
              style={{
                color: "#111827",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {formatMetaValue(val)}
            </span>
          </div>
        ))}
      </div>
    );
  };

  return (
    <BatchesContainer>
      <BatchesControls>
        <ControlGroup>
          <ControlLabel htmlFor="batch-ordering">Sort by:</ControlLabel>
          <ControlSelect
            id="batch-ordering"
            value={ordering}
            onChange={handleOrderingChange}
          >
            <option value="-created_at">Newest created</option>
            <option value="created_at">Oldest created</option>
            <option value="name">Name A-Z</option>
            <option value="-name">Name Z-A</option>
          </ControlSelect>
        </ControlGroup>

        <ControlGroup>
          <ControlLabel htmlFor="batch-search">Search:</ControlLabel>
          <SearchInputWrapper>
            <Search size={15} />
            <ControlInput
              id="batch-search"
              type="text"
              placeholder="Search by name"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  handleSearchSubmit(e);
                }
              }}
            />
          </SearchInputWrapper>
        </ControlGroup>

        <ControlsSpacer />

        <ButtonGroup>
          <RefreshButton
            type="button"
            className={isLoading ? "disabled" : ""}
            onClick={() => fetchBatches(null, false)}
            disabled={isLoading}
          >
            {isLoading ? "Refreshing..." : "Refresh"}
          </RefreshButton>

          <CreateBatchButton type="button" onClick={() => navigate("/upload")}>
            Create New Batch
          </CreateBatchButton>
        </ButtonGroup>
      </BatchesControls>

      {isLoading && <BatchesStatus>Loading batches...</BatchesStatus>}
      {error && <BatchesError>{error}</BatchesError>}
      {!isLoading && !error && batches.length === 0 && (
        <BatchesStatus>No batches found.</BatchesStatus>
      )}

      {!isLoading && !error && batches.length > 0 && (
        <>
          <TableContainer>
            <TableScrollArea>
              <Table>
                <TableHead>
                  <tr>
                    <TableHeader>ID</TableHeader>
                    <TableHeader>Batch</TableHeader>
                    <TableHeader>Cards Count</TableHeader>
                    <TableHeader>Actions</TableHeader>
                  </tr>
                </TableHead>
                <TableBody>
                  {batches.map((batch, id) => (
                  <TableRow
                    key={id}
                    onClick={() =>
                      navigate(`/batches/${encodeURIComponent(batch.name)}`)
                    }
                  >
                    <IdColumn>{(currentPage - 1) * pageSize + id + 1}</IdColumn>
                    <NameColumn
                      onClick={(e) => {
                        e.stopPropagation();
                        if (editingBatchId !== batch.id)
                          startEditingName(batch);
                      }}
                    >
                      {editingBatchId === batch.id ? (
                        <EditContainer onClick={(e) => e.stopPropagation()}>
                          <InlineInput
                            value={editNameValue}
                            onChange={(e) => setEditNameValue(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") {
                                e.stopPropagation();
                                saveEditingName(batch);
                              } else if (e.key === "Escape") {
                                e.stopPropagation();
                                cancelEditingName();
                              }
                            }}
                            autoFocus
                          />
                          <RowActions>
                            <SmallButton
                              onClick={(e) => {
                                e.stopPropagation();
                                saveEditingName(batch);
                              }}
                            >
                              Save
                            </SmallButton>
                            <SmallButton
                              variant="secondary"
                              onClick={(e) => {
                                e.stopPropagation();
                                cancelEditingName();
                              }}
                            >
                              Cancel
                            </SmallButton>
                          </RowActions>
                        </EditContainer>
                      ) : (
                        batch.name
                      )}
                    </NameColumn>
                    <CardsCountColumn
                      style={{ textAlign: "left", paddingLeft: "1.2rem" }}
                    >
                      {typeof batch.card_count === "number" ? batch.card_count : 0}
                    </CardsCountColumn>
                    <ActionsColumn>
                      <RowActions>
                        <IconButton
                          title="View batch"
                          aria-label="View batch"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(
                              `/batches/${encodeURIComponent(batch.name)}`
                            );
                          }}
                        >
                          <Eye size={15} />
                        </IconButton>
                        <IconButton
                          variant="danger"
                          title="Delete batch"
                          aria-label="Delete batch"
                          onClick={(e) => {
                            e.stopPropagation();
                            openDeleteModal(batch);
                          }}
                        >
                          <Trash2 size={15} />
                        </IconButton>
                      </RowActions>
                    </ActionsColumn>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            </TableScrollArea>
          </TableContainer>

          {/* Pagination controls */}
          <BatchesPagination>
            <PaginationArrow
              onClick={() => prevPage && fetchBatches(prevPage)}
              disabled={!prevPage}
              className={!prevPage ? "disabled" : ""}
              aria-label="Previous page"
              title="Previous page"
            >
              <ChevronLeft size={15} />
            </PaginationArrow>

            {getPageNumbers(currentPage, Math.ceil(count / pageSize)).map(
              (page, idx) =>
                typeof page === "number" ? (
                  <PageButton
                    key={page}
                    $active={page === currentPage}
                    onClick={() =>
                      page !== currentPage && fetchBatches(buildPageUrl(page))
                    }
                  >
                    {page}
                  </PageButton>
                ) : (
                  <PageEllipsis key={`${page}-${idx}`}>...</PageEllipsis>
                )
            )}

            <PaginationArrow
              onClick={() => nextPage && fetchBatches(nextPage)}
              disabled={!nextPage}
              className={!nextPage ? "disabled" : ""}
              aria-label="Next page"
              title="Next page"
            >
              <ChevronRight size={15} />
            </PaginationArrow>
          </BatchesPagination>
        </>
      )}

      {/* Delete Confirmation Modal */}
      {deleteModalOpen && batchToDelete && (
        <ModalOverlay onClick={closeDeleteModal}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalTitle>Delete Batch</ModalTitle>
            <ModalMessage>
              Are you sure you want to delete the batch "{batchToDelete.name}"?
              This action cannot be undone.
            </ModalMessage>
            <ModalActions>
              <ModalButton onClick={closeDeleteModal}>Cancel</ModalButton>
              <ModalButton variant="danger" onClick={confirmDelete}>
                Delete
              </ModalButton>
            </ModalActions>
          </ModalContent>
        </ModalOverlay>
      )}
    </BatchesContainer>
  );
}
